import styles from '../shared.module.scss';
import { useFormik } from 'formik';
import { Button, Divider, Select, TextInput } from 'src/components/design-system';
import { WORKSPACE_SCHEMA_SCHEDULE_URLS } from '../workspace-editor/workspace.schema';
import { MODES } from '../workspace-general-info';
import { useProfileOptionsField } from 'shared/hooks/profile.hooks';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { useState } from 'react';
import { LabelsSelector } from 'src/components/labels/labels-selector';

/**
 * ScheduleUrl editor component
 * @param {object} props
 * @param {import('../../types').ScheduleUrl} props.scheduleUrl the schedule url to edit
 * @param {'create' | 'edit'} props.mode the mode of the editor
 * @param {(values: import('../../types').ScheduleUrl) => Promise<void>} props.onSubmit function to submit the schedule url
 * @param {() => void} props.onCancel function to cancel the editor
 */
export function ScheduleUrlEditor({ scheduleUrl, mode, onSubmit, onCancel }) {
  const lang = useSelector(getLang('SETTINGS'));
  const [loading, setLoading] = useState(false);

  const applyChanges = async (values, { resetForm }) => {
    setLoading(true);
    await onSubmit(values);
    setLoading(false);
    resetForm();
  };

  const { values, errors, handleSubmit, setFieldValue, isValid, dirty, resetForm } = useFormik({
    initialValues: !scheduleUrl
      ? {
          roles: [],
          professions: [],
          labels: [],
          url: '',
          title: '',
        }
      : { ...scheduleUrl },
    validationSchema: WORKSPACE_SCHEMA_SCHEDULE_URLS,
    enableReinitialize: true,
    onSubmit: applyChanges,
  });

  const { professionOptions, roleOptions } = useProfileOptionsField();

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.fields}>
        <TextInput
          label={lang.QGENDA_TITLE_LABEL}
          name="title"
          value={values.title}
          error={errors.title}
          onChange={e => setFieldValue('title', e.target.value)}
        />

        <TextInput
          label={lang.QGENDA_URL}
          name="url"
          value={values.url}
          error={errors.url}
          onChange={e => setFieldValue('url', e.target.value)}
          required
        />

        <Select
          label={lang.PROFESSIONS}
          name="professions"
          value={values.professions}
          onChange={setFieldValue}
          size="large"
          options={professionOptions}
          mode="tags"
        />

        <Select
          label={lang.ROLES}
          name="roles"
          value={values.roles}
          onChange={setFieldValue}
          size="large"
          options={roleOptions}
          mode="tags"
        />

        <LabelsSelector
          label={lang.SITES}
          name="labels"
          value={values.labels}
          onChange={setFieldValue}
          error={errors.labels}
          placeholder={lang.SELECT_LABELS}
          className={styles.labelsSelector}
        />
      </div>

      <Divider />

      <div className={styles.actions}>
        <Button type="default" onClick={handleCancel}>
          {lang.CANCEL}
        </Button>
        <Button loading={loading} type="primary" htmlType="submit" disabled={!isValid || !dirty}>
          {mode === MODES.CREATE ? lang.ADD_LINK : lang.APPLY_CHANGES}
        </Button>
      </div>
    </form>
  );
}
